import { useApolloClient } from '@apollo/client';
import { useEffect } from 'react';

import {
  CONFIG,
  extractDataFromQuery,
  extractIdFromQuery,
  getProcessStepsQuery,
  getProcessStepsUpdateSubscription,
  ProcessStepsAutoRefreshVariant,
} from './useProcessStepsAutoRefresh.util';

export interface UseProcessStepsAutoRefreshProps {
  variant: ProcessStepsAutoRefreshVariant;
  // pass id if you are only interested in a specific negotiation case (like on negotiation case-detail)
  id?: string;
}

/**
 * subscribes to process steps update events
 * and updates the apollo-cache for the related entities
 */
export const useProcessStepsAutoRefresh = ({ id, variant }: UseProcessStepsAutoRefreshProps): void => {
  const client = useApolloClient();

  const handleRefresh = async (entityId: string) => {
    const { fragment, typeName } = CONFIG[variant];
    const id = `${typeName}:${entityId}`;

    // check if apollo has cached the negotiation case
    const cached = client.readFragment({ id, fragment });

    // only update the cached state of that might be visible
    const query = getProcessStepsQuery(variant);
    if (cached && query) {
      // reload data from server
      const { data, errors } = await client.query({
        query,
        variables: { id: entityId },
        fetchPolicy: 'no-cache',
      });
      if (!errors) {
        // update cache
        client.writeFragment({
          id,
          fragment,
          data: extractDataFromQuery(variant, data),
        });
      }
    }
  };

  useEffect(() => {
    const query = getProcessStepsUpdateSubscription(variant);
    if (query) {
      const observer = client
        .subscribe({
          query,
        })
        .subscribe({
          next: ({ data }) => {
            const entityId = extractIdFromQuery(variant, data);
            if (entityId && (entityId === id || id === undefined)) {
              handleRefresh(entityId);
            }
          },
        });

      return () => {
        observer.unsubscribe();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, id]);
};
